import React, { useState, useEffect } from 'react';
import { 
  Play, 
  ExternalLink, 
  AlertTriangle, 
  CheckCircle2, 
  ClipboardList, 
  Sparkles, 
  Tv, 
  Info,
  Maximize2,
  Video
} from 'lucide-react';
import { 
  getSafeEmbedUrl, 
  inspectEmbedUrl, 
  getEmbedSandbox, 
  EMBED_ALLOW_POLICY, 
  fetchVideoMetadata, 
  VideoMetadata 
} from '../services/embedService';
import { useThemeTokens } from '../hooks/useActiveTheme';

export interface EmbeddedPhasePlayerProps {
  embedMode?: 'url' | 'code';
  embedUrl?: string;
  embedCode?: string;
  aspectRatio?: '16:9' | '4:3' | 'custom';
  embedWidth?: string;
  embedHeight?: string;
  title?: string;
  videoTitle?: string;
  videoAuthor?: string;
  videoAuthorUrl?: string;
  videoThumbnail?: string;
  videoNotes?: string;
  videoDescription?: string;
  className?: string;
}

export const EmbeddedPhasePlayer: React.FC<EmbeddedPhasePlayerProps> = ({
  embedMode = 'url',
  embedUrl = '',
  embedCode = '',
  aspectRatio = '16:9',
  embedWidth = '100%',
  embedHeight = '550px',
  title = '',
  videoTitle: propVideoTitle,
  videoAuthor: propVideoAuthor,
  videoAuthorUrl: propVideoAuthorUrl,
  videoNotes,
  videoDescription,
  className = ''
}) => {
  const theme = useThemeTokens();
  const [fetchedMeta, setFetchedMeta] = useState<VideoMetadata | null>(null);
  const [isExtracting, setIsExtracting] = useState<boolean>(false);

  // Auto-fetch video metadata if not yet saved on the slide
  useEffect(() => {
    let isMounted = true;
    if (embedMode === 'url' && embedUrl && (!propVideoTitle || !propVideoAuthor)) {
      const details = inspectEmbedUrl(embedUrl);
      if (details.isValid && !details.isBlockedRoot) {
        setIsExtracting(true);
        fetchVideoMetadata(embedUrl)
          .then((meta) => {
            if (isMounted && meta) {
              setFetchedMeta(meta);
            }
          })
          .catch(() => {})
          .finally(() => {
            if (isMounted) setIsExtracting(false);
          });
      }
    }
    return () => {
      isMounted = false;
    };
  }, [embedUrl, embedMode, propVideoTitle, propVideoAuthor]);

  // Derived metadata
  const effectiveTitle = propVideoTitle || fetchedMeta?.title || title || 'Clinical Video Demonstration';
  const effectiveAuthor = propVideoAuthor || fetchedMeta?.authorName || '';
  const effectiveAuthorUrl = propVideoAuthorUrl || fetchedMeta?.authorUrl || '';
  const effectiveNotes = videoNotes || videoDescription || fetchedMeta?.description || '';

  // Inspect URL
  const details = embedMode === 'url' ? inspectEmbedUrl(embedUrl) : null;
  const safeUrl = embedMode === 'url' ? getSafeEmbedUrl(embedUrl) : '';
  const isYouTube = details?.provider === 'youtube';

  // Sizing and aspect ratio container calculations
  const effectiveRatio = aspectRatio || fetchedMeta?.aspectRatio || '16:9';
  const isCustomRatio = effectiveRatio === 'custom';

  return (
    <div className={`w-full max-w-5xl mx-auto space-y-4 ${className}`}>
      {/* Professional Media Stage Header */}
      {embedMode === 'url' && embedUrl && !details?.isBlockedRoot && (
        <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-3 p-4 bg-white dark:bg-slate-900 border border-slate-200 dark:border-slate-800 rounded-2xl shadow-xs transition-all">
          <div className="flex items-start sm:items-center gap-3.5 min-w-0">
            <div className="p-2.5 rounded-xl shadow-xs shrink-0 flex items-center justify-center bg-indigo-600 dark:bg-indigo-500 text-white shadow-indigo-500/20">
              <Video size={18} />
            </div>

            <div className="min-w-0 space-y-0.5">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-[9px] font-black uppercase tracking-wider px-2.5 py-0.5 rounded-md bg-indigo-50 text-indigo-700 dark:bg-indigo-950/50 dark:text-indigo-300 border border-indigo-200/80 dark:border-indigo-900/60">
                  Clinical Demonstration
                </span>
                {effectiveAuthor && (
                  <span className="inline-flex items-center gap-1.5 text-[10px] font-black tracking-wide text-slate-700 dark:text-slate-200 bg-slate-100 dark:bg-slate-800 px-2.5 py-0.5 rounded-md border border-slate-200/80 dark:border-slate-700/80 shadow-2xs">
                    <span className="text-slate-400 dark:text-slate-500 uppercase text-[9px] font-bold">Demonstration By:</span>
                    {effectiveAuthorUrl ? (
                      <a 
                        href={effectiveAuthorUrl}
                        target="_blank"
                        rel="noopener noreferrer"
                        className="hover:text-indigo-600 dark:hover:text-indigo-400 transition-colors underline decoration-dotted font-extrabold"
                      >
                        {effectiveAuthor}
                      </a>
                    ) : (
                      <span className="font-extrabold">{effectiveAuthor}</span>
                    )}
                  </span>
                )}
                {isExtracting && (
                  <span className="text-[10px] text-indigo-600 animate-pulse font-semibold">
                    (syncing meta...)
                  </span>
                )}
              </div>
              <h3 className="text-sm md:text-base font-extrabold text-slate-900 dark:text-white truncate">
                {effectiveTitle}
              </h3>
            </div>
          </div>

          <div className="flex items-center gap-2 shrink-0 self-end sm:self-auto">
            {/* Aspect Ratio Badge */}
            <span className="hidden md:inline-flex items-center gap-1 px-2.5 py-1 bg-slate-100 dark:bg-slate-800 text-slate-600 dark:text-slate-300 text-[10px] font-bold rounded-lg border border-slate-200 dark:border-slate-700">
              <Tv size={12} className="text-slate-400" />
              <span>{effectiveRatio === '16:9' ? '16:9 Widescreen' : (effectiveRatio === '4:3' ? '4:3 Standard' : 'Custom')}</span>
            </span>

            {/* External source link */}
            {embedUrl && (
              <a 
                href={embedUrl}
                target="_blank"
                rel="noopener noreferrer"
                className="inline-flex items-center gap-1.5 px-3 py-1.5 bg-slate-100 hover:bg-slate-200 dark:bg-slate-800 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-200 rounded-xl text-xs font-bold transition-all shadow-xs border border-slate-200 dark:border-slate-700 active:scale-95"
                title="Open video in source player"
              >
                <span>External Source</span>
                <ExternalLink size={12} className="text-slate-400" />
              </a>
            )}
          </div>
        </div>
      )}

      {/* Main Video Viewport Stage */}
      <div 
        className="w-full rounded-2xl md:rounded-[2rem] overflow-hidden border border-slate-200/90 dark:border-slate-800 bg-slate-950 shadow-2xl relative"
        style={isCustomRatio ? { width: embedWidth || '100%', height: embedHeight || '550px', minHeight: '360px' } : undefined}
      >
        {embedMode === 'url' && embedUrl ? (
          (() => {
            if (details?.isBlockedRoot || !safeUrl) {
              return (
                <div className="min-h-[380px] flex flex-col items-center justify-center text-slate-400 p-8 text-center space-y-4 bg-slate-900/90">
                  <div className="w-14 h-14 rounded-2xl bg-amber-500/20 text-amber-400 flex items-center justify-center shadow-lg">
                    <AlertTriangle size={28} />
                  </div>
                  <div className="space-y-1.5 max-w-md">
                    <h4 className="text-base font-extrabold text-white">Specific Video Link Needed</h4>
                    <p className="text-xs text-slate-300 leading-relaxed">
                      The base YouTube home page (<code className="bg-slate-800 px-1 py-0.5 rounded text-amber-300">https://www.youtube.com/</code>) sets <code className="bg-slate-800 px-1 py-0.5 rounded text-amber-300">X-Frame-Options: SAMEORIGIN</code> and cannot be embedded in an iframe.
                    </p>
                    <p className="text-[11px] text-slate-400">
                      Please supply a specific video link (e.g. <code className="text-indigo-300">https://www.youtube.com/watch?v=...</code>) or click below to launch YouTube.
                    </p>
                  </div>
                  {embedUrl && (
                    <a 
                      href={embedUrl} 
                      target="_blank" 
                      rel="noopener noreferrer" 
                      className="inline-flex items-center gap-2 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white rounded-xl text-xs font-bold transition-all shadow-lg active:scale-95 cursor-pointer"
                    >
                      <ExternalLink size={14} /> Open in YouTube Tab
                    </a>
                  )}
                </div>
              );
            }

            // Aspect ratio inner frame container
            const ratioContainerClass = effectiveRatio === '16:9'
              ? 'relative w-full aspect-video bg-black flex items-center justify-center'
              : (effectiveRatio === '4:3'
                ? 'relative w-full aspect-[4/3] bg-black flex items-center justify-center'
                : 'relative w-full h-full bg-black flex items-center justify-center');

            return (
              <div className={ratioContainerClass}>
                <iframe 
                  src={safeUrl} 
                  title={effectiveTitle}
                  className={isCustomRatio ? "w-full h-full border-none shadow-inner" : "absolute inset-0 w-full h-full border-none shadow-inner"}
                  sandbox={getEmbedSandbox(safeUrl)}
                  allow={EMBED_ALLOW_POLICY}
                  referrerPolicy="strict-origin-when-cross-origin"
                  allowFullScreen
                />
              </div>
            );
          })()
        ) : embedMode === 'code' && embedCode ? (
          <div className="relative w-full min-h-[420px] bg-slate-900">
            <iframe 
              srcDoc={embedCode} 
              title={title || "Embedded Resource"}
              className="w-full h-full min-h-[420px] border-none shadow-inner bg-slate-100"
              sandbox="allow-scripts allow-popups allow-forms allow-same-origin allow-presentation allow-popups-to-escape-sandbox"
              allow={EMBED_ALLOW_POLICY}
              allowFullScreen
            />
          </div>
        ) : (
          <div className="min-h-[380px] flex flex-col items-center justify-center text-slate-400 p-8 text-center space-y-3 bg-slate-900/60">
            <Info size={44} className="opacity-20 animate-pulse text-white" />
            <p className="text-xs font-black tracking-widest uppercase opacity-40">No Embedded Media Mapped</p>
          </div>
        )}
      </div>

      {/* Clinical Notes & Takeaways Card */}
      {effectiveNotes && (
        <div className="p-5 md:p-6 bg-gradient-to-br from-indigo-50/70 to-slate-50 dark:from-slate-900/80 dark:to-slate-800/60 border border-indigo-100/90 dark:border-indigo-900/40 rounded-2xl md:rounded-[1.75rem] shadow-xs space-y-3">
          <div className="flex items-center justify-between">
            <div className="flex items-center gap-2 text-indigo-950 dark:text-indigo-300 font-extrabold text-sm">
              <div className="p-1.5 rounded-lg bg-indigo-600 text-white shadow-xs">
                <ClipboardList size={15} />
              </div>
              <span>Clinical Protocol Highlights & Key Takeaways</span>
            </div>
            <span className="text-[10px] font-black uppercase tracking-wider text-indigo-600 dark:text-indigo-400 bg-indigo-100/60 dark:bg-indigo-950/60 px-2.5 py-0.5 rounded-full border border-indigo-200/50 dark:border-indigo-900/50">
              Exam Checkpoints
            </span>
          </div>
          <div className="text-xs md:text-sm text-slate-700 dark:text-slate-300 leading-relaxed whitespace-pre-line space-y-1.5 font-medium pl-1">
            {effectiveNotes}
          </div>
          <div className="pt-2 border-t border-indigo-100/60 dark:border-indigo-900/30 flex items-center gap-1.5 text-[11px] text-slate-500 dark:text-slate-400">
            <CheckCircle2 size={13} className="text-emerald-500 shrink-0" />
            <span>Master these clinical rationale steps before proceeding to the practical simulator challenge.</span>
          </div>
        </div>
      )}
    </div>
  );
};
